<template>
    <div class="schedule-summary border rounded p-4 bg-white" style="max-width: 100%;">
        <div class="row">
            <div class="col-md-6">
                <h5 class="mb-3" style="font-size: 16px; font-weight: 700;">Schedule Summary</h5>
                <div class="summary-row mb-2 mt-3" style="font-size: 14px; --label-width: 120px;">
                    <span class="summary-label text-muted">Report Template:</span>
                    <span class="summary-value">
                        {{ formattedCategory }} - {{ reportData.template_name || 'Default' }}
                    </span>
                </div>
                <div class="summary-row mb-2" style="font-size: 14px; --label-width: 120px;">
                    <span class="summary-label text-muted">Recipients:</span>
                    <span class="summary-value">{{ recipientCount }}</span>
                </div>
                <div class="summary-row mb-2" style="font-size: 14px; --label-width: 120px;">
                    <span class="summary-label text-muted">Created By:</span>
                    <span class="summary-value">{{ reportData.created_by || 'Me' }}</span>
                </div>
            </div>
            <div class="col-md-6 pt-5">
                <div class="summary-row mb-2" style="font-size: 14px; --label-width: 100px;">
                    <span class="summary-label text-muted">Frequency:</span>
                    <span class="summary-value">{{ formattedFrequency }}</span>
                </div>
                <div class="summary-row mb-2" style="font-size: 14px; --label-width: 100px;">
                    <span class="summary-label text-muted">{{ deliveryLabel }}:</span>
                    <span class="summary-value">{{ formattedDeliveryDate }}</span>
                </div>
            </div>
        </div>
        
    </div>
</template>

<script>
import moment from 'moment-timezone';

const FIXED_TIME_ZONE = 'Etc/GMT+5';

export default {
    name: 'ScheduleSummaryCard',
    props: {
        reportData: {
            type: Object,
            required: true,
            default: () => ({ email_settings: {} })
        }
    },
    computed: {
        // Remove the Report:: prefix for display
        formattedCategory() {
            const category = this.reportData.category || '';
            return category.replace('Report::', '');
        },
        // Count of recipients
        recipientCount() {
            return this.reportData.email_settings?.recipients?.length || 0;
        },
        // User-friendly schedule string
        formattedFrequency() {
            const type = this.reportData.schedule_type;
            const time = this.reportData.schedule_time;
            const day = this.reportData.schedule_day;

            if (!type || !time || !day) return '-';

            // Format time 09:00 -> 9:00 AM
            const timeMoment = moment.tz(time, 'HH:mm', FIXED_TIME_ZONE);
            const timeStr = timeMoment.isValid() ? timeMoment.format('h:mm A') : time;
            const timeWithZone = `${timeStr} CST`;

            if (type === 'weekly') {
                const dayName = day.charAt(0).toUpperCase() + day.slice(1);
                return `Weekly, every ${dayName} at ${timeWithZone}`;
            }
            return `Monthly, every 1st day of the month at ${timeWithZone}`;
        },
        deliveryLabel() {
            return this.hasPreviousRun ? 'Next delivery' : 'First delivery';
        },
        hasPreviousRun() {
            return !!this.scheduleLastRunAt;
        },
        scheduleNextRunAt() {
            return this.reportData?.report_schedule_schedules?.next_run_at || this.reportData?.schedule_next_run_at || null;
        },
        scheduleLastRunAt() {
            return this.reportData?.report_schedule_schedules?.last_run_at || this.reportData?.schedule_last_run_at || null;
        },
        // Compute the next/first delivery date string.
        // FE note: if backend `next_run_at` is missing, we derive it here from
        // `schedule_type` + `schedule_day` + `schedule_time` (client-side preview only).
        formattedDeliveryDate() {
            if (this.scheduleNextRunAt) {
                const date = moment(this.scheduleNextRunAt).tz(FIXED_TIME_ZONE);
                return date.isValid() ? date.format('dddd, MMMM D, YYYY') : '-';
            }
            const type = this.reportData.schedule_type;
            const time = this.reportData.schedule_time || '09:00';
            const day = this.reportData.schedule_day || 'monday';

            if (!time) return '-';

            const now = moment().tz(FIXED_TIME_ZONE);
            const timeParts = time.split(':');
            const hours = parseInt(timeParts[0]) || 9;
            const minutes = parseInt(timeParts[1]) || 0;

            let nextDate = moment().tz(FIXED_TIME_ZONE);
            nextDate.hours(hours).minutes(minutes).seconds(0);

            if (type === 'weekly') {
                const targetDay = now.day(day).isoWeekday();
                nextDate = nextDate.isoWeekday(targetDay);
                // Always schedule the next occurrence, not the same day.
                if (nextDate.isSame(now, 'day') || nextDate.isBefore(now)) {
                    nextDate.add(1, 'week');
                }
            } else {
                nextDate.date(1);
                if (nextDate.isBefore(now)) {
                    nextDate.add(1, 'months');
                }
            }

            return nextDate.format('dddd, MMMM D, YYYY');
        },
    }
}
</script>

<style scoped>
.summary-row {
    display: flex;
    align-items: flex-start;
    column-gap: 8px;
}

.summary-label {
    flex: 0 0 var(--label-width, 120px);
    max-width: var(--label-width, 120px);
}

.summary-value {
    flex: 1 1 auto;
    min-width: 0;
    color: #3F3F3F;
    font-weight: 500;
    white-space: normal;
    word-break: break-word;
}
</style>
